import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  createSupportTicket,
  getSupportTicket,
  listSupportTickets,
  replyToSupportTicket,
  resolveSupportTicket,
} from '@/api/services/support-tickets.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import type { CreateTicketPayload, TicketDetail } from '@/types/support-ticket.types'

export const supportTicketQueryKeys = {
  /** Param-less prefix: invalidate this after any mutation so list + details refresh. */
  all: ['support-tickets'] as const,
  list: () => ['support-tickets', 'list'] as const,
  detail: (id: string) => ['support-tickets', 'detail', id] as const,
}

export function useSupportTickets() {
  return useQuery({
    queryKey: supportTicketQueryKeys.list(),
    queryFn: listSupportTickets,
    staleTime: 30 * 1000,
  })
}

export function useSupportTicket(id: string | undefined) {
  return useQuery({
    queryKey: supportTicketQueryKeys.detail(id ?? ''),
    queryFn: () => getSupportTicket(id!),
    enabled: !!id,
    staleTime: 15 * 1000,
    // A missing / foreign ticket is a 404 — retrying won't change that.
    retry: false,
  })
}

/** Seeds the detail cache with the server's fresh copy, then refreshes every ticket query. */
function useSyncTicket() {
  const queryClient = useQueryClient()
  return (ticket: TicketDetail) => {
    queryClient.setQueryData(supportTicketQueryKeys.detail(ticket.id), ticket)
    void queryClient.invalidateQueries({ queryKey: supportTicketQueryKeys.all })
  }
}

export function useCreateSupportTicket() {
  const sync = useSyncTicket()
  return useMutation({
    mutationFn: (payload: CreateTicketPayload) => createSupportTicket(payload),
    onSuccess: (ticket) => {
      sync(ticket)
      toast.success(`Ticket ${ticket.reference} sent to KAAMCO support`)
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useReplyToSupportTicket(id: string) {
  const sync = useSyncTicket()
  return useMutation({
    mutationFn: (body: string) => replyToSupportTicket(id, body),
    onSuccess: (ticket) => {
      sync(ticket)
      toast.success('Reply sent')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}

export function useResolveSupportTicket(id: string) {
  const sync = useSyncTicket()
  return useMutation({
    mutationFn: () => resolveSupportTicket(id),
    onSuccess: (ticket) => {
      sync(ticket)
      toast.success('Ticket marked as resolved')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
